import { Injectable, inject } from '@angular/core';
import { PriceDtoV2, SubscriptionPricingDto } from '@nusign/picflow-backend-types';
import { ISbStoryData } from '@storyblok/js';
import { firstValueFrom, from, lastValueFrom, map } from 'rxjs';
import { GROWTHBOOK } from '../growthbook';
import { PICFLOW_STORYBLOK } from '../picflow-storyblok';
import { Product } from '../types/product.type';
import { PricingService } from './pricing.service';

@Injectable({
  providedIn: 'root',
})
export class ProductService {
  private storyblokService = inject(PICFLOW_STORYBLOK);
  private pricingService = inject(PricingService);
  private growthBook = inject(GROWTHBOOK);

  public async getAllProducts(): Promise<Product[]> {
    const [products, pricingInfo] = await Promise.all([
      lastValueFrom(this.storyblokService.getStories({ content_type: 'product' }).pipe(map(resp => resp.stories))),
      firstValueFrom(this.pricingService.pricingInfo$),
    ]);
    return products.map(product => this.convertProduct(product, pricingInfo)).filter(Boolean);
  }

  private convertProduct(storyblokProduct: ISbStoryData, pricingInfo: SubscriptionPricingDto): Product {
    const allPricesForProduct = pricingInfo.prices.filter(
      price =>
        price.product_type === storyblokProduct.slug &&
        (price.product === 'main' || (price.product as string) === 'free' || (price.product as string) === 'enterprise')
    );
    const prices = allPricesForProduct;

    const isEnterprise = allPricesForProduct.some(p => p.product_type === 'enterprise');
    const isFree = allPricesForProduct.some(p => p.product_type === 'free');
    const seatAddonPrices = pricingInfo.prices.filter(
      price => price.product === 'seat_addon' && price.product_type === storyblokProduct.slug
    );

    const product: Product = {
      id: storyblokProduct.slug,
      storyblokProductId: storyblokProduct.uuid,
      name: storyblokProduct.name,
      prices: prices,
      isFree,
      isEnterprise,
      color: storyblokProduct.content.color,
      description: storyblokProduct.content.description,
      highlights: storyblokProduct.content.highlights,
      seatAddonPrices,
    };
    return product;
  }

  public getFeaturedProducts() {
    return this.getProductsForKey('featured-products');
  }

  public getAllListedProducts() {
    return this.getProductsForKey('all-listed-products');
  }

  public getProductsForKey(keyword: string) {
    return from(this.getAllProducts()).pipe(
      map(allProducts => {
        const defaultProductNames = this.growthBook.getFeatureValue(keyword, []);
        return defaultProductNames
          .map(name => allProducts.find(p => p.name.toLowerCase().includes(name.toLowerCase())))
          .filter(Boolean);
      })
    );
  }

  public async getRecommendedUpgradeProduct(productType: PriceDtoV2['product_type']): Promise<Product> {
    const [defaultProducts] = await Promise.all([firstValueFrom(this.getFeaturedProducts())]);
    const recommendedProductType = (() => {
      if (!productType) return 'plus';
      if (productType === 'plus') return 'pro';
    })();
    if (!recommendedProductType) return;
    return defaultProducts.find(product => product.id === recommendedProductType);
  }

  public getDefaultHighlightedProductName() {
    return this.growthBook.getFeatureValue('highlighted-product', 'pro');
  }
}
